import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Eye, EyeSlash, Buildings } from '@phosphor-icons/react'
import { subscribeToPushNotifications } from '../utils/pushNotifications'

export default function Signup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [validatingToken, setValidatingToken] = useState(true)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [orgData, setOrgData] = useState(null)
  const [inviteSubgroupName, setInviteSubgroupName] = useState(null)
  const [inviteData, setInviteData] = useState(null)

  // Get token or org from URL
  const token = searchParams.get('token')
  const orgId = searchParams.get('org')
  const groupId = searchParams.get('group') // Legacy parameter
  const prefillEmail = searchParams.get('email') || ''
  const prefillName = searchParams.get('name') || ''
  const prefillPhone = searchParams.get('phone') || ''
  const memberType = searchParams.get('type') || 'new'
  const prefillRole = searchParams.get('role') || 'member'
  const prefillLeadershipLevel = searchParams.get('leadership_level') || null
  const prefillTeamId = searchParams.get('team_id') || null
  const prefillGroupId = searchParams.get('group_id') || null
  const prefillSubgroupId = searchParams.get('subgroup_id') || null
  const inviteSubgroupId = searchParams.get('invite_subgroup') || null // NEW: For subgroup invites

  const [formData, setFormData] = useState({
    name: prefillName,
    email: prefillEmail,
    phone: prefillPhone,
    password: '',
    confirmPassword: ''
  })

  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })

  useEffect(() => {
    if (token) {
      // New token-based flow
      validateToken()
    } else if (!orgId) {
      // Old flow without token or org
      setError('Invalid invite link. Please contact your administrator.')
      setValidatingToken(false)
    } else {
      // Old flow with org parameter
      setValidatingToken(false)
      fetchOrgData()
      if (inviteSubgroupId) {
        fetchInviteSubgroupName()
      }
    }
  }, [token, orgId, inviteSubgroupId])

  const validateToken = async () => {
    setValidatingToken(true)
    setError('')

    try {
      // Validate token in invite_links table
      const { data: invite, error: inviteError } = await supabase
        .from('invite_links')
        .select('*')
        .eq('token', token)
        .eq('used', false)
        .gt('expires_at', new Date().toISOString())
        .single()

      if (inviteError || !invite) {
        setError('This invite link is invalid or has expired. Please contact your administrator for a new invite link.')
        setValidatingToken(false)
        return
      }

      // Valid invite - store data and fetch org info
      setInviteData(invite)
      
      // Pre-fill email from invite
      setFormData(prev => ({
        ...prev,
        email: invite.recipient_email || ''
      }))

      // Fetch organization data
      const { data: org } = await supabase
        .from('organizations')
        .select('name, logo_url')
        .eq('id', invite.org_id)
        .single()

      if (org) setOrgData(org)
      
      setValidatingToken(false)
    } catch (error) {
      console.error('Token validation error:', error)
      setError('Failed to validate invite link. Please try again.')
      setValidatingToken(false)
    }
  }

  const fetchOrgData = async () => {
    try {
      const orgIdToFetch = inviteData?.org_id || orgId
      if (!orgIdToFetch) return

      const { data } = await supabase
        .from('organizations')
        .select('name, logo_url')
        .eq('id', orgIdToFetch)
        .single()
      
      if (data) setOrgData(data)
    } catch (error) {
      console.error('Error fetching org data:', error)
    }
  }

  const fetchInviteSubgroupName = async () => {
    try {
      const { data } = await supabase
        .from('groups')
        .select('name')
        .eq('id', inviteSubgroupId)
        .single()
      
      if (data) setInviteSubgroupName(data.name)
    } catch (error) {
      console.error('Error fetching subgroup:', error)
    }
  }

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required'
        if (value.trim().length < 2) return 'Name must be at least 2 characters'
        return ''
      case 'email':
        if (!value.trim()) return 'Email is required'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address'
        return ''
      case 'phone':
        if (value && value.trim() && !/^[\d\s\-\+\(\)]+$/.test(value)) return 'Please enter a valid phone number'
        return ''
      case 'password':
        if (!value) return 'Password is required'
        if (value.length < 6) return 'Password must be at least 6 characters'
        if (!/(?=.*[a-z])/.test(value)) return 'Password must contain at least one lowercase letter'
        if (!/(?=.*[A-Z])/.test(value)) return 'Password must contain at least one uppercase letter'
        if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number'
        return ''
      case 'confirmPassword':
        if (!value) return 'Please confirm your password'
        if (value !== formData.password) return 'Passwords do not match'
        return ''
      default:
        return ''
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }))
    }
    setError('')
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    const error = validateField(name, value)
    setFieldErrors(prev => ({ ...prev, [name]: error }))
  }

  const validateForm = () => {
    const errors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      phone: validateField('phone', formData.phone),
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    }
    setFieldErrors(errors)
    return !Object.values(errors).some(error => error !== '')
  }

const handleSignup = async (e) => {
  e.preventDefault()
  setError('')

  if (!validateForm()) {
    setError('Please fix the errors above')
    return
  }

  setLoading(true)
  let authUserId = null // Track the auth user ID

  try {
    // Step 1: Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: formData.email.trim(),
      password: formData.password,
    })

    if (authError) {
      if (authError.message.includes('already registered')) {
        setError('This email is already registered. Please login instead.')
      } else {
        setError(authError.message)
      }
      return
    }

    authUserId = authData.user.id // Store the ID
    
    // Determine role and settings based on member type
    let role = 'unassigned'
    let leadershipLevel = null
    let qrSignature = null
    let qrGeneratedAt = null

    // NEW: Handle invite_subgroup signup
    if (inviteSubgroupId) {
      role = 'member'
      qrSignature = `${authUserId}-${Date.now()}`
      qrGeneratedAt = new Date().toISOString()
    } else if (memberType === 'existing') {
      role = prefillRole
      leadershipLevel = prefillLeadershipLevel
      
      if (prefillSubgroupId || prefillGroupId) {
        qrSignature = `${authUserId}-${Date.now()}`
        qrGeneratedAt = new Date().toISOString()
      }
    } else if (groupId) {
      role = 'member'
      qrSignature = `${authUserId}-${Date.now()}`
      qrGeneratedAt = new Date().toISOString()
    }

    // Step 2: Insert user record
    const finalOrgId = inviteData?.org_id || orgId
    
    console.log('📝 Signup data check:', {
      inviteData_orgId: inviteData?.org_id,
      orgId: orgId,
      finalOrgId: finalOrgId,
      authUserId: authUserId
    })
    
    // CRITICAL: Validate org_id exists
    if (!finalOrgId) {
      throw new Error('Organization ID is missing. Please use a valid invite link.')
    }
    
    const { error: userError } = await supabase
      .from('users')
      .insert({
        id: authUserId,
        email: formData.email.trim(),
        name: formData.name.trim(),
        phone: formData.phone.trim() || null,
        role: role,
        leadership_level: leadershipLevel,
        org_id: finalOrgId,
        qr_signature: qrSignature,
        qr_generated_at: qrGeneratedAt,
        status: 'active'
      })

    if (userError) {
      console.error('❌ User insert error:', userError)
      throw userError
    }
    
    console.log('✅ User record created successfully:', authUserId)

    // Mark invite token as used if this was a token-based signup
    if (token && inviteData) {
      const { error: inviteUpdateError } = await supabase
        .from('invite_links')
        .update({
          used: true,
          used_by: authUserId,
          used_at: new Date().toISOString()
        })
        .eq('token', token)
      
      if (inviteUpdateError) {
        console.error('Failed to mark invite as used:', inviteUpdateError)
        // Don't throw - user is already created, just log the error
        // Admin can manually mark as used if needed
      } else {
        console.log('Invite marked as used successfully:', token)
      }
    }

    // Step 3: Insert group memberships
    
    // NEW: Handle invite_subgroup assignment
    if (inviteSubgroupId) {
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          user_id: authUserId,
          group_id: inviteSubgroupId
        })

      if (memberError) throw memberError
      
      // Mark any pending invitations as accepted
      await supabase
        .from('invitations')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString()
        })
        .eq('invitee_email', formData.email.trim().toLowerCase())
        .eq('subgroup_id', inviteSubgroupId)
        .eq('status', 'pending')
        
    } else if (memberType === 'existing') {
      const groupInserts = []
      
      if (prefillTeamId) {
        groupInserts.push({
          user_id: authUserId,
          group_id: prefillTeamId
        })
      }
      
      if (prefillGroupId) {
        groupInserts.push({
          user_id: authUserId,
          group_id: prefillGroupId
        })
      }
      
      if (prefillSubgroupId) {
        groupInserts.push({
          user_id: authUserId,
          group_id: prefillSubgroupId
        })
      }
      
      if (groupInserts.length > 0) {
        const { error: memberError } = await supabase
          .from('group_members')
          .insert(groupInserts)
        
        if (memberError) throw memberError
      }
    } else if (groupId) {
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          user_id: authUserId,
          group_id: groupId
        })

      if (memberError) throw memberError
    }

    // ========================================================================
    // NEW: SUBSCRIBE TO PUSH NOTIFICATIONS
    // ========================================================================
    console.log('📱 Subscribing to push notifications...')
    
    try {
      await subscribeToPushNotifications(authUserId)
      console.log('✅ Push notification subscription complete')
    } catch (pushError) {
      // Don't fail signup if push subscription fails
      console.error('⚠️ Push subscription failed (non-critical):', pushError)
    }
    // ========================================================================

    // Success!
    alert('Account created successfully! You will receive notifications about events.')
    navigate(`/login?org=${finalOrgId}`)

  } catch (error) {
    console.error('❌ Signup error:', error)
    
    // CRITICAL: If we created an auth user but failed later, clean it up
    if (authUserId) {
      try {
        console.log('🧹 Cleaning up orphaned auth user:', authUserId)
        
        // Call edge function to delete the orphaned auth user
        const { error: cleanupError } = await supabase.functions.invoke('cleanup-auth-user', {
          body: { userId: authUserId }
        })
        
        if (cleanupError) {
          console.error('Cleanup failed:', cleanupError)
        } else {
          console.log('✅ Orphaned auth user cleaned up successfully')
        }
      } catch (cleanupError) {
        console.error('Error during cleanup:', cleanupError)
      }
    }
    
    // User-friendly error messages
    let userMessage = 'Failed to create account. '
    
    if (error.message && error.message.includes('Organization ID is missing')) {
      userMessage = 'Invalid invite link. Please contact your administrator for a new invite link.'
    } else if (error.code === '23503') {
      userMessage = 'Invalid organization. Please contact your administrator.'
    } else if (error.code === '23505') {
      userMessage = 'This email is already registered. Please login instead.'
    } else {
      userMessage += error.message || 'Please try again or contact support.'
    }
    
    setError(userMessage)
  } finally {
    setLoading(false)
  }
}

  // Show loading while validating token
  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    )
  }

  // Show error if no valid org or invite
  if (!orgId && !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invite Link</h2>
          <p className="text-gray-600 mb-6">
            This invite link is invalid or has expired. Please contact your administrator for a new invite link.
          </p>
          <a href="/login" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to Login
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Organization Info */}
          {orgData && (
            <div className="flex flex-col items-center mb-6">
              {orgData.logo_url ? (
                <img 
                  src={orgData.logo_url} 
                  alt={orgData.name}
                  className="h-16 w-16 object-contain mb-3"
                />
              ) : (
                <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                  <Buildings size={32} weight="light" className="text-blue-600" />
                </div>
              )}
              <h3 className="text-xl font-bold text-gray-900">{orgData.name}</h3>
            </div>
          )}
          
          <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
          <p className="mt-2 text-sm text-gray-600">Join your organization</p>
        </div>

        <form onSubmit={handleSignup} className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input 
              type="text" 
              id="name" 
              name="name" 
              required 
              value={formData.name} 
              onChange={handleChange} 
              onBlur={handleBlur} 
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.name ? 'border-red-300' : 'border-gray-300'}`} 
              placeholder="John Doe" 
              disabled={loading} 
            />
            {fieldErrors.name && <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email address *</label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              required 
              value={formData.email} 
              onChange={handleChange} 
              onBlur={handleBlur} 
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.email ? 'border-red-300' : 'border-gray-300'}`} 
              placeholder="you@example.com" 
              disabled={loading} 
            />
            {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input 
              type="tel" 
              id="phone" 
              name="phone" 
              value={formData.phone} 
              onChange={handleChange} 
              onBlur={handleBlur} 
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.phone ? 'border-red-300' : 'border-gray-300'}`} 
              placeholder="+1 (555) 123-4567" 
              disabled={loading} 
            />
            {fieldErrors.phone && <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
            <div className="relative">
              <input 
                type={showPassword ? 'text' : 'password'} 
                id="password" 
                name="password" 
                required 
                value={formData.password} 
                onChange={handleChange} 
                onBlur={handleBlur} 
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.password ? 'border-red-300' : 'border-gray-300'}`} 
                placeholder="••••••••" 
                disabled={loading} 
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.password && <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>}
            <p className="mt-1 text-xs text-gray-500">Must contain at least 6 characters, including uppercase, lowercase, and a number</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
            <div className="relative">
              <input 
                type={showConfirmPassword ? 'text' : 'password'} 
                id="confirmPassword" 
                name="confirmPassword" 
                required 
                value={formData.confirmPassword} 
                onChange={handleChange} 
                onBlur={handleBlur} 
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`} 
                placeholder="••••••••" 
                disabled={loading} 
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.confirmPassword && <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>}
          </div>

          {/* Info boxes based on member type */}
          {inviteSubgroupId && inviteSubgroupName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                ✓ You're signing up to join <strong>{inviteSubgroupName}</strong>. You'll be automatically assigned after registration.
              </p>
            </div>
          )}

          {memberType === 'existing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">✓ Your role and team assignment have been pre-configured. You'll be ready to use the system immediately after registration.</p>
            </div>
          )}

          {memberType === 'new' && !inviteSubgroupId && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">ⓘ You are signing up as a new member. An administrator will assign you to a team after signup.</p>
            </div>
          )}

          {groupId && !memberType && !inviteSubgroupId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">✓ You will be automatically assigned to your group and receive a QR code for attendance.</p>
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading} 
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>

          <p className="text-center text-gray-600 text-sm mt-6">
            Already have an account?{' '}
            <a href={`/login?org=${orgId}`} className="text-blue-600 hover:text-blue-700 font-medium">
              Login
            </a>
          </p>
        </form>
      </div>
    </div>
  )
}